import { useState } from 'react'
import { motion } from 'framer-motion'
import ScrollReveal from '../components/shared/ScrollReveal'
import GradientText from '../components/shared/GradientText'
import GlowButton from '../components/shared/GlowButton'
import { Icons } from '../components/shared/Icons'
import { COUNTRY_CODES } from '../data/countryCodes'
import './DraftPages.css'

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xnjdpgkd'

export default function Contact() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    countryCode: '+1',
    phone: '',
    jobTitle: '',
    message: ''
  })
  const [status, setStatus] = useState('idle') // idle, submitting, success, error

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setStatus('submitting')

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          _subject: `Contact from ${formData.firstName} ${formData.lastName} - ${formData.company}`
        })
      })

      if (response.ok) {
        setStatus('success')
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          company: '',
          countryCode: '+1',
          phone: '',
          jobTitle: '',
          message: ''
        })
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  return (
    <div className="draft-page">
      <section className="draft-hero demo-hero">
        <div className="container">
          <div className="demo-grid">
            <div className="demo-content">
              <ScrollReveal>
                <span className="draft-badge">Get in Touch</span>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <h1 className="draft-title">
                  Contact{' '}
                  <GradientText gradient="solar">Us</GradientText>
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={0.2}>
                <p className="draft-subtitle">
                  Have questions about AI4Solar? We&apos;re here to help.
                  Reach out and our team will get back to you promptly.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.3} className="demo-benefits">
                <div className="demo-benefit">
                  <span className="benefit-icon">
                    <Icons.activity size={24} />
                  </span>
                  <div>
                    <span className="benefit-title">24-hour response</span>
                    <span className="benefit-desc">We reply within one business day</span>
                  </div>
                </div>
                <div className="demo-benefit">
                  <span className="benefit-icon">
                    <Icons.target size={24} />
                  </span>
                  <div>
                    <span className="benefit-title">Expert support team</span>
                    <span className="benefit-desc">Solar industry specialists ready to help</span>
                  </div>
                </div>
                <div className="demo-benefit">
                  <span className="benefit-icon">
                    <Icons.chart size={24} />
                  </span>
                  <div>
                    <span className="benefit-title">No commitment required</span>
                    <span className="benefit-desc">Just a friendly conversation</span>
                  </div>
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal animation="fadeRight" className="demo-form-wrapper">
              <motion.form
                className="demo-form"
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
              >
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="company">Company *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Phone (Optional)</label>
                  <div className="phone-input-group">
                    <select
                      id="countryCode"
                      name="countryCode"
                      value={formData.countryCode}
                      onChange={handleChange}
                      className="form-input form-select country-code-select"
                    >
                      {COUNTRY_CODES.map((cc) => (
                        <option key={`${cc.code}-${cc.country}`} value={cc.code}>
                          {cc.code} ({cc.country})
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input phone-input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="jobTitle">Job Title</label>
                  <input
                    type="text"
                    id="jobTitle"
                    name="jobTitle"
                    value={formData.jobTitle}
                    onChange={handleChange}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">How can we help? *</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="form-input form-textarea"
                    placeholder="Tell us about your questions, feedback, or how we can assist you..."
                  />
                </div>

                <GlowButton
                  type="submit"
                  variant="solar"
                  size="large"
                  className="form-submit"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Sending...' : 'Send Message'}
                </GlowButton>

                {status === 'success' && (
                  <p className="form-success">
                    Thank you! We&apos;ll be in touch within 24 hours.
                  </p>
                )}

                {status === 'error' && (
                  <p className="form-error">
                    Something went wrong. Please try again or email us directly.
                  </p>
                )}

                <p className="form-note">
                  We&apos;ll respond within 24 hours. By submitting, you agree to our Privacy Policy.
                </p>
              </motion.form>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  )
}
